import { useRef, useReducer } from 'react';
import type { FormRendererHandle, FormHandleMethods } from '../types/renderer';
import type { FormEngineStatus } from '../store/engineReducer';

let nextId = 0;

interface RefState {
  isDirty: boolean;
  isSubmitting: boolean;
  status: FormEngineStatus;
}

interface UseFormRendererRefOptions {
  /** When true the engine's built-in submit bar is suppressed. Defaults to true (legacy behaviour). */
  hideSubmitBar?: boolean;
}

/**
 * Creates a **stable** handle for `FormRenderer` that auto-tracks
 * `isDirty`, `isSubmitting`, `status`, and `isLoading`.
 *
 * The returned object identity NEVER changes across renders — safe to pass
 * as a prop without causing child re-renders. Getter properties read from
 * React state so the *consumer* re-renders when values change, but the
 * handle reference itself is stable.
 *
 * IMPORTANT: Pass as `formHandle` prop (NOT `ref`). React silently strips
 * `ref` on plain function components that are not wrapped in `forwardRef`,
 * so `props.ref` would always be `undefined`.
 *
 * @example
 * ```tsx
 * const formHandle = useFormRendererRef();
 *
 * <ModalSheet
 *   loading={formHandle.isLoading}
 *   actionForm={formHandle.formId}
 *   dirty={formHandle.isDirty}
 *   actionLoading={formHandle.isSubmitting}
 *   actionDisabled={!formHandle.isDirty}
 * >
 *   <FormRenderer formHandle={formHandle} schema={schema} mode='edit' />
 * </ModalSheet>
 * ```
 */
export function useFormRendererRef(options?: UseFormRendererRefOptions): FormRendererHandle {
  const hideSubmitBar = options?.hideSubmitBar ?? true;
  const [state, dispatch] = useReducer(
    (prev: RefState, next: RefState) =>
      prev.isDirty === next.isDirty &&
      prev.isSubmitting === next.isSubmitting &&
      prev.status === next.status
        ? prev
        : next,
    { isDirty: false, isSubmitting: false, status: 'idle' as FormEngineStatus },
  );

  const stateRef = useRef(state);
  stateRef.current = state;

  const methodsRef = useRef<FormHandleMethods | null>(null);

  const handle = useRef<FormRendererHandle | null>(null);
  if (handle.current === null) {
    const formId = `fe-form-${++nextId}`;
    handle.current = {
      formId,
      get isDirty() { return stateRef.current.isDirty; },
      get isSubmitting() { return stateRef.current.isSubmitting; },
      get status() { return stateRef.current.status; },
      get isLoading() {
        return stateRef.current.status === 'fetching';
      },
      triggerValidation: async () => {
        if (!methodsRef.current) return false;
        return methodsRef.current.trigger();
      },
      getFormValues: () => {
        if (!methodsRef.current) return {};
        return methodsRef.current.getValues();
      },
      submitForm: async () => {
        if (!methodsRef.current) throw new Error('Form not mounted');
        return methodsRef.current.handleSubmit();
      },
      _setState: (dirty, submitting, status) =>
        dispatch({ isDirty: dirty, isSubmitting: submitting, status }),
      _hideSubmitBar: hideSubmitBar,
      _registerFormMethods: (methods: FormHandleMethods) => {
        methodsRef.current = methods;
      },
    };
  }

  return handle.current;
}
